import { hasUnpairedPlayer } from '@/core/helpers/unpaired.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import { hasPlayedPosition } from '@/views/main/handshakes/utils/handshake-game';
import { storeToRefs } from 'pinia';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

/**
 * One preview chip for a card whose voters or attendees include someone the
 * viewer has never played a position with. The detail lists keep the badge on
 * each person, so this stays off the shared tag helper.
 */
export function useUnpairedChip(
  playerIds: MaybeRefOrGetter<Array<string | null | undefined>>,
  size: GamChipSize = GamChipSize.X_SMALL,
) {
  const authStore = useAuthStore();
  const { user, userDetails } = storeToRefs(authStore);
  const liveHandshakes = useLiveHandshakesStore();

  return computed((): GamChipType | null => {
    const unpaired = hasUnpairedPlayer(toValue(playerIds), {
      viewerId: user.value?.id,
      positionPartnerIds: userDetails.value?.positionPartnerIds,
      hasPlayedLivePosition: (playerId) => liveHandshakes.withPeer(playerId).some(hasPlayedPosition),
    });

    if (!unpaired) return null;

    return {
      label: 'attendee.badge.unpaired',
      size,
      variant: GamChipVariant.PRIMARY,
      isActive: true,
      isUpperCase: true,
    };
  });
}
